import { Component, Inject } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import {
  DateRange,
  DefaultMatCalendarRangeStrategy,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
} from '@angular/material/datepicker';
import * as moment from 'moment';
import { ConfigService } from 'src/app/service/config.service';
import { MondayFirstDateAdapter } from 'src/app/shared/utils/monday-first-date-adapter';
import { RoutingService } from 'src/app/service/routing.service';
import { INTERNAL_COCKPIT_SETTINGS } from 'src/app/shared/utils/cockpit-constants';

export const DATE_RANGE_FORMATS = {
  parse: { dateInput: 'DD-MM-YYYY' },
  display: {
    dateInput: 'DD-MM-YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

const WIRE_FORMAT = 'YYYY-MM-DD';
const DISPLAY_FORMAT = 'DD MMM YYYY';
/** The fields are read-only: the calendars below are the only way to set them. */
const PLACEHOLDER = 'Select on calendar';
/** Chart smoothing switches to the coarse window past a month. */
const COARSE_PERIOD_THRESHOLD_DAYS = 30;

export interface DateRangeDialogData {
  /** Current `time` param — a `from--to` range pre-selects it. */
  time?: string;
}

export interface DateRangeDialogResult {
  time: string;
  period: string;
}

/**
 * Custom timeframe picker: two Material calendars sharing one range selection.
 * Replaces the legacy alert()-based validation with a disabled Apply plus an
 * inline reason.
 */
@Component({
  selector: 'app-date-range-dialog',
  templateUrl: './date-range-dialog.component.html',
  styleUrls: ['./date-range-dialog.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MondayFirstDateAdapter,
    },
    { provide: MAT_DATE_FORMATS, useValue: DATE_RANGE_FORMATS },
    // One strategy instance, reached two ways: injected here to advance the
    // range on click, and under the token so the month views draw the hover
    // preview from the same object.
    DefaultMatCalendarRangeStrategy,
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useExisting: DefaultMatCalendarRangeStrategy,
    },
  ],
})
export class DateRangeDialogComponent {
  readonly minDate: moment.Moment;
  readonly maxDate: moment.Moment;
  readonly maxSpanDays = INTERNAL_COCKPIT_SETTINGS.maxTimeframe;

  range: DateRange<moment.Moment>;
  /** Left calendar month; the right one always trails it by one. */
  leftStartAt: moment.Moment;
  rightStartAt: moment.Moment;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: DateRangeDialogData | null,
    private dialogRef: MatDialogRef<
      DateRangeDialogComponent,
      DateRangeDialogResult
    >,
    private rangeStrategy: DefaultMatCalendarRangeStrategy<moment.Moment>,
    private routing: RoutingService,
    config: ConfigService,
  ) {
    this.minDate = moment(config.appConfig.setup.startDate).startOf('day');
    this.maxDate = moment().startOf('day');
    this.range = this.initialRange(data?.time);

    const anchor = this.range.end ?? this.range.start ?? this.maxDate;
    // Anchor on the right calendar so the selection stays visible without
    // scrolling, and never let the right pane run past today's month.
    this.rightStartAt = moment.min(anchor.clone(), this.maxDate.clone());
    this.leftStartAt = this.rightStartAt.clone().subtract(1, 'month');
  }

  get startLabel(): string {
    return this.range.start
      ? this.range.start.format(DISPLAY_FORMAT)
      : PLACEHOLDER;
  }

  get endLabel(): string {
    return this.range.end ? this.range.end.format(DISPLAY_FORMAT) : PLACEHOLDER;
  }

  /** Which end of the range the next calendar click lands on. */
  get activeStep(): 'start' | 'end' {
    return this.range.start && !this.range.end ? 'end' : 'start';
  }

  get spanDays(): number | null {
    if (!this.range.start || !this.range.end) return null;
    return this.range.end.diff(this.range.start, 'days');
  }

  /** Non-null when Apply is blocked; rendered as the inline hint. */
  get invalidReason(): string | null {
    if (!this.range.start || !this.range.end) {
      return null;
    }
    const span = this.spanDays ?? 0;
    if (!this.routing.isDev() && span > this.maxSpanDays) {
      return `The date range cannot exceed ${this.maxSpanDays} days.`;
    }
    return null;
  }

  /** Error where there is one, otherwise the next step to take. */
  get hint(): string | null {
    if (this.invalidReason) return this.invalidReason;
    if (!this.range.start) return 'Click a start date on the calendar.';
    if (!this.range.end) return 'Now click an end date.';
    return null;
  }

  get canApply(): boolean {
    return (
      !!this.range.start && !!this.range.end && this.invalidReason === null
    );
  }

  onDateSelected(date: moment.Moment | null): void {
    if (!date) return;
    this.range = this.rangeStrategy.selectionFinished(date, this.range);
  }

  /** Clicking the Start field re-arms the range so the next click sets a start. */
  resetSelection(): void {
    this.range = new DateRange<moment.Moment>(null, null);
  }

  /** Clicking the End field keeps the start and re-arms only the end. */
  resetEnd(): void {
    if (!this.range.start) return;
    this.range = new DateRange<moment.Moment>(this.range.start, null);
  }

  apply(): void {
    if (!this.canApply || !this.range.start || !this.range.end) return;
    const span = this.spanDays ?? 0;
    this.dialogRef.close({
      time: `${this.range.start.format(WIRE_FORMAT)}--${this.range.end.format(
        WIRE_FORMAT,
      )}`,
      period: span > COARSE_PERIOD_THRESHOLD_DAYS ? '14' : '7',
    });
  }

  private initialRange(time?: string): DateRange<moment.Moment> {
    if (time?.includes('--')) {
      const [from, to] = time.split('--');
      const start = moment(from, WIRE_FORMAT, true);
      const end = moment(to, WIRE_FORMAT, true);
      if (start.isValid() && end.isValid()) {
        return new DateRange(
          moment.max(start, this.minDate.clone()),
          moment.min(end, this.maxDate.clone()),
        );
      }
    }
    return new DateRange<moment.Moment>(null, null);
  }
}
